/**
 * Page-level layout (DESIGN.md §9.2): the compact PageHeader (breadcrumb · title · actions on
 * one line, then optional tabs and a toolbar row, all sticky), PageTabs (deep-linkable page
 * tabs), TabbedPage (a page whose tabs mount other page components), SummaryStrip (KPI chips)
 * and PageBody (content width rules, §9.1).
 *
 * Embedding: a page component mounted as a tab of a TabbedPage keeps rendering its own
 * `PageHeader`. Inside the tab that header gives up its title (the TabbedPage owns it) and moves
 * its actions, status and toolbar into the TabbedPage's sticky header, so page components need no
 * changes to become tabs.
 */
import { ChevronDownIcon, ChevronRightIcon, CircleDashedIcon } from 'lucide-react';
import { createContext, Fragment, useContext, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';
import type { BreadcrumbItem } from '../navigation';
import { useUiStrings } from '../strings';
import { Button } from '../components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/dropdown-menu';
import { ShellLink } from './link';

export interface PageHeaderProps {
  /** Full path; the last item is the current page and is not a link. */
  breadcrumb?: readonly BreadcrumbItem[];
  title: string;
  description?: string;
  /** Status badge next to the title. */
  status?: ReactNode;
  /** Primary action (one default-variant Button) followed by secondary actions. */
  actions?: ReactNode;
  /** Tabs directly under the header (settings and detail pages). */
  tabs?: ReactNode;
  /** Sticky row under the tabs: filters, search, bulk actions. */
  toolbar?: ReactNode;
}

interface EmbedHosts {
  actions: HTMLElement | null;
  toolbar: HTMLElement | null;
}

/** Set by TabbedPage: headers inside it render into these hosts. */
const EmbedContext = createContext<EmbedHosts | undefined>(undefined);

export function PageHeader(props: PageHeaderProps) {
  const embed = useContext(EmbedContext);
  return embed ? <EmbeddedHeader {...props} hosts={embed} /> : <StandaloneHeader {...props} />;
}

function Breadcrumb({ items }: { items: readonly BreadcrumbItem[] }) {
  const strings = useUiStrings();
  if (items.length === 0) return null;
  return (
    <nav aria-label={strings.breadcrumb} className="hidden min-w-0 sm:block">
      <ol className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
        {items.map((crumb, index) => (
          <Fragment key={`${crumb.label}-${index}`}>
            {index > 0 && <ChevronRightIcon aria-hidden className="size-3 shrink-0" />}
            <li className="truncate">
              {crumb.href && index < items.length - 1 ? (
                <ShellLink href={crumb.href} className="hover:text-foreground hover:underline">
                  {crumb.label}
                </ShellLink>
              ) : (
                <span aria-current={index === items.length - 1 ? 'page' : undefined}>{crumb.label}</span>
              )}
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}

function HeaderFrame({
  breadcrumb = [],
  title,
  description,
  status,
  actions,
  tabs,
  toolbar,
  actionsRef,
  toolbarRef,
}: PageHeaderProps & { actionsRef?: (node: HTMLElement | null) => void; toolbarRef?: (node: HTMLElement | null) => void }) {
  // The breadcrumb ends with the current page, which the title shows: only the levels above it are drawn.
  const parents = breadcrumb.at(-1)?.label === title ? breadcrumb.slice(0, -1) : breadcrumb;
  return (
    <header className="sticky top-0 z-10 flex flex-col border-b bg-background/95 px-page backdrop-blur">
      <div className="flex min-h-bar flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <Breadcrumb items={parents} />
          {parents.length > 0 && <ChevronRightIcon aria-hidden className="hidden size-3 shrink-0 text-muted-foreground sm:block" />}
          <h1 className="truncate text-base font-semibold tracking-tight">{title}</h1>
          {status}
        </div>
        <div ref={actionsRef} className="flex flex-wrap items-center gap-1.5 empty:hidden">
          {actions}
        </div>
      </div>
      {description && <p className="-mt-1 line-clamp-2 pb-2 text-xs text-muted-foreground">{description}</p>}
      {tabs}
      <div ref={toolbarRef} className="flex flex-col empty:hidden">
        {toolbar && <div className="flex flex-wrap items-center gap-2 py-1.5">{toolbar}</div>}
      </div>
    </header>
  );
}

function StandaloneHeader(props: PageHeaderProps) {
  return <HeaderFrame {...props} />;
}

/** Header of a page mounted as a tab: no title of its own; actions and toolbar join the outer header. */
function EmbeddedHeader({ description, status, actions, tabs, toolbar, hosts }: PageHeaderProps & { hosts: EmbedHosts }) {
  const headerActions = (status || actions) && (
    <>
      {status}
      {actions}
    </>
  );
  const headerRows = (tabs || toolbar) && (
    <>
      {tabs}
      {toolbar && <div className="flex flex-wrap items-center gap-2 py-1.5">{toolbar}</div>}
    </>
  );
  return (
    <>
      {headerActions && hosts.actions && createPortal(headerActions, hosts.actions)}
      {headerRows && hosts.toolbar && createPortal(headerRows, hosts.toolbar)}
      {description && <p className="px-page pt-3 text-xs text-muted-foreground">{description}</p>}
    </>
  );
}

export interface PageTab {
  id: string;
  label: string;
  href: string;
  /** Registered but not built: muted with a marker; opens a placeholder. */
  planned?: boolean;
}

/** Page tabs as links (deep-linkable, middle-click opens a new browser tab). */
export function PageTabs({ tabs, activeId, label }: { tabs: readonly PageTab[]; activeId: string; label: string }) {
  const strings = useUiStrings();
  const nav = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const element = nav.current;
    const active = element?.querySelector('[aria-current="page"]');
    if (!element || !active) return;
    const reveal = () => {
      const bounds = element.getBoundingClientRect();
      const selected = active.getBoundingClientRect();
      // Scroll only the tab strip, preserving the page's own scroll position.
      if (selected.right > bounds.right) element.scrollLeft += selected.right - bounds.right;
      else if (selected.left < bounds.left) element.scrollLeft -= bounds.left - selected.left;
    };
    reveal();
    // Portal actions, fonts and responsive panels can change the available width after mounting.
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(reveal);
    observer?.observe(element);
    observer?.observe(active);
    return () => observer?.disconnect();
  }, [activeId]);
  const activeTab = tabs.find((tab) => tab.id === activeId);
  return (
    <>
      <nav ref={nav} aria-label={label} className="-mb-px hidden h-control items-stretch gap-1 overflow-x-auto md:flex">
        {tabs.map((tab) => (
          <ShellLink
            key={tab.id}
            href={tab.href}
            aria-current={tab.id === activeId ? 'page' : undefined}
            className={cn(
              'inline-flex items-center gap-1 border-b-2 border-transparent px-2 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
              tab.id === activeId && 'border-primary text-foreground',
              tab.planned && tab.id !== activeId && 'font-normal text-muted-foreground/80',
            )}
          >
            <PageTabLabel tab={tab} plannedLabel={strings.planned} />
          </ShellLink>
        ))}
      </nav>
      <nav aria-label={label} className="-mb-px flex min-w-0 items-center md:hidden">
        {tabs.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-control min-w-0 max-w-full justify-start gap-2 px-2">
                <span className="inline-flex min-w-0 items-center gap-1">
                  <span className="truncate">{activeTab?.label ?? label}</span>
                  {activeTab?.planned && (
                    <>
                      <CircleDashedIcon aria-hidden className="size-3 shrink-0 opacity-70" />
                      <span className="sr-only">({strings.planned})</span>
                    </>
                  )}
                </span>
                <ChevronDownIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" aria-label={label} className="max-h-sheet w-[min(20rem,calc(100vw-2rem))] overflow-y-auto">
              {tabs.map((tab) => (
                <DropdownMenuItem key={tab.id} asChild>
                  <ShellLink href={tab.href} aria-current={tab.id === activeId ? 'page' : undefined}>
                    <PageTabLabel tab={tab} plannedLabel={strings.planned} />
                  </ShellLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </nav>
    </>
  );
}

function PageTabLabel({ tab, plannedLabel }: { tab: PageTab; plannedLabel: string }) {
  return (
    <>
      {tab.label}
      {tab.planned && (
        <>
          <CircleDashedIcon aria-hidden className="size-3 opacity-70" />
          <span className="sr-only">({plannedLabel})</span>
        </>
      )}
    </>
  );
}

export interface TabbedPageProps {
  title: string;
  breadcrumb?: readonly BreadcrumbItem[];
  description?: string;
  tabs: readonly PageTab[];
  activeId: string;
  /** Accessible name of the tab bar (usually the page title). */
  tabsLabel?: string;
  /** Page-wide actions; the active tab's own actions are added after them. */
  actions?: ReactNode;
  /** KPI strip under the header (SummaryStrip). */
  summary?: ReactNode;
  /** The active tab's content: a page component (it may render its own PageHeader). */
  children: ReactNode;
}

/** A page whose tabs are page components (DESIGN.md §9.2): one header, one tab bar, one sticky toolbar. */
export function TabbedPage({ title, breadcrumb, description, tabs, activeId, tabsLabel, actions, summary, children }: TabbedPageProps) {
  const [actionsHost, setActionsHost] = useState<HTMLElement | null>(null);
  const [toolbarHost, setToolbarHost] = useState<HTMLElement | null>(null);
  return (
    <>
      <HeaderFrame
        title={title}
        breadcrumb={breadcrumb}
        description={description}
        actions={actions}
        actionsRef={setActionsHost}
        toolbarRef={setToolbarHost}
        tabs={tabs.length > 1 ? <PageTabs tabs={tabs} activeId={activeId} label={tabsLabel ?? title} /> : undefined}
      />
      {summary && <div className="px-page pt-3">{summary}</div>}
      <EmbedContext.Provider value={{ actions: actionsHost, toolbar: toolbarHost }}>{children}</EmbedContext.Provider>
    </>
  );
}

export type SummaryTone = 'default' | 'ok' | 'warning' | 'danger';

export interface SummaryItem {
  id: string;
  label: string;
  value: ReactNode;
  tone?: SummaryTone;
  /** Chips are filters: clicking applies one (DESIGN.md §9.2 summary strip). */
  onSelect?: () => void;
  active?: boolean;
}

const toneClass: Record<SummaryTone, string> = {
  default: 'text-foreground',
  ok: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
};

/** KPI strip under a page header: compact value chips, optionally acting as filters. */
export function SummaryStrip({ items, label }: { items: readonly SummaryItem[]; label: string }) {
  return (
    <ul aria-label={label} className="flex flex-wrap gap-2">
      {items.map((item) => {
        const body = (
          <>
            <span className={cn('text-sm font-semibold tabular-nums', toneClass[item.tone ?? 'default'])}>{item.value}</span>
            <span className="text-xs text-muted-foreground">{item.label}</span>
          </>
        );
        const chip = 'flex h-control items-center gap-1.5 rounded-md border bg-card px-2.5';
        return (
          <li key={item.id}>
            {item.onSelect ? (
              <button
                type="button"
                aria-pressed={item.active ?? false}
                onClick={item.onSelect}
                className={cn(chip, 'outline-none hover:bg-accent focus-visible:ring-3 focus-visible:ring-ring/50', item.active && 'border-primary bg-primary/5')}
              >
                {body}
              </button>
            ) : (
              <div className={chip}>{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface PageBodyProps {
  /** `full` for lists, dashboards and three-pane; `form` (max-w-3xl) for forms and settings. */
  width?: 'full' | 'form';
  className?: string;
  children: ReactNode;
}

export function PageBody({ width = 'full', className, children }: PageBodyProps) {
  return <div className={cn('flex flex-col gap-section p-page', width === 'form' && 'max-w-3xl', className)}>{children}</div>;
}
